import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type MouseEvent,
} from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import InsightsIcon from "@mui/icons-material/Insights";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PaymentsIcon from "@mui/icons-material/Payments";
import PointOfSaleOutlinedIcon from "@mui/icons-material/PointOfSaleOutlined";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import moment from "moment";
import { Shell } from "../../common/shell/shell.page";
import { PageHeader } from "../../common/components/page-header";
import { LoadingOverlay } from "../../common/components/loading-overlay";
import application from "../../core/application";
import { Permission } from "../../users/user.entity";
import { sellRepository } from "../sell.repository";
import { Sell } from "../sell.entity";
import { buildSalesSummaryForRange, formatBRL } from "../utils/sales-analytics";
import { SalesAnalyticsHero } from "../components/sales-analytics-hero";
import {
  emptyPeriodInsight,
  generateSalesInsights,
} from "../../ai/sales-insights";
import { useModuleAccess } from "../../settings/module-access";
import { ModuleLockedView } from "../../settings/components/module-locked-view";
import { ReportPeriodSelector } from "../components/report-period-selector";
import {
  type ReportPeriodPreset,
  computePresetRange,
  formatPeriodHuman,
  parseCustomRange,
} from "../utils/report-period";

const CHART_COLORS = ["#0d9488", "#2563eb", "#7c3aed", "#ea580c", "#64748b"];
const SELLS_QUERY_LIMIT = 10000;

export const SalesAnalyticsPage = () => {
  application.useHasPermission([Permission.SELL, Permission.READ_SALES_ANALYTICS]);

  const theme = useTheme();
  const moduleAccess = useModuleAccess();
  const aiInsightsEnabled = moduleAccess.isEnabled("bulk_register");

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [sells, setSells] = useState<Sell[]>([]);
  const [hitQueryLimit, setHitQueryLimit] = useState(false);

  const [periodPreset, setPeriodPreset] =
    useState<ReportPeriodPreset>("current_month");
  const [customFrom, setCustomFrom] = useState(() =>
    moment().startOf("month").format("YYYY-MM-DD")
  );
  const [customTo, setCustomTo] = useState(() => moment().format("YYYY-MM-DD"));

  const [aiText, setAiText] = useState<string | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);

  const dateRange = useMemo(() => {
    if (periodPreset === "custom") {
      return parseCustomRange(customFrom, customTo);
    }
    return computePresetRange(periodPreset);
  }, [periodPreset, customFrom, customTo]);

  const periodLabel = useMemo(
    () => formatPeriodHuman(dateRange.start, dateRange.end),
    [dateRange.start, dateRange.end]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const list = await sellRepository.getCompletedSellsInDateRange(
          dateRange.start,
          dateRange.end,
          SELLS_QUERY_LIMIT
        );
        if (cancelled) return;
        setHitQueryLimit(list.length >= SELLS_QUERY_LIMIT);
        setSells(list);
      } catch (e) {
        if (!cancelled) {
          setLoadError(
            e instanceof Error ? e.message : "Não foi possível carregar as vendas."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dateRange.start, dateRange.end]);

  const summary = useMemo(
    () => buildSalesSummaryForRange(sells, dateRange.start, dateRange.end),
    [sells, dateRange.start, dateRange.end]
  );

  const handlePresetChange = (
    _e: MouseEvent<HTMLElement>,
    value: ReportPeriodPreset | null
  ) => {
    if (value === null) return;
    if (value === "custom" && periodPreset !== "custom") {
      const seed =
        periodPreset === "last_month" ||
          periodPreset === "current_month" ||
          periodPreset === "last_3_months" ?
          computePresetRange(periodPreset) :
          computePresetRange("current_month");
      setCustomFrom(moment(seed.start).format("YYYY-MM-DD"));
      setCustomTo(moment(seed.end).format("YYYY-MM-DD"));
    }
    if (value !== "custom") {
      const r = computePresetRange(value);
      setCustomFrom(moment(r.start).format("YYYY-MM-DD"));
      setCustomTo(moment(r.end).format("YYYY-MM-DD"));
    }
    setPeriodPreset(value);
  };

  const runInsights = useCallback(async () => {
    if (!aiInsightsEnabled) return;
    setAiLoading(true);
    setAiError(null);
    try {
      if (summary.saleCount === 0) {
        setAiText(emptyPeriodInsight(summary.periodLabelHuman));
        return;
      }
      const text = await generateSalesInsights(summary);
      setAiText(text);
    } catch (e) {
      setAiError(
        e instanceof Error ? e.message : "Falha ao gerar insights com IA."
      );
    } finally {
      setAiLoading(false);
    }
  }, [aiInsightsEnabled, summary]);

  if (moduleAccess.loading) {
    return (
      <Shell title="Estatísticas de vendas">
        <LoadingOverlay message="Carregando módulo…" subtle />
      </Shell>
    );
  }

  if (!moduleAccess.isEnabled("statistics_reports")) {
    return (
      <Shell title="Estatísticas de vendas">
        <PageHeader title="Estatísticas de vendas" description="Análises e relatórios comerciais." />
        <ModuleLockedView
          moduleName="Estatísticas e Relatórios"
          groupId="statistics_reports"
          details="Ative este módulo para acessar gráficos, indicadores e insights das vendas."
          features={["Gráficos de vendas por período", "Insights com IA Gemini", "Relatórios exportáveis"]}
        />
      </Shell>
    );
  }

  return (
    <Shell title="Estatísticas de vendas">
      {loading ? <LoadingOverlay message="Carregando vendas…" subtle /> : null}
      {aiLoading ? <LoadingOverlay subtle message="Gerando insights com IA…" /> : null}

      <PageHeader
        title="Estatísticas de vendas"
        description="Receita, tendência diária, formas de pagamento e produtos que mais puxam resultado — no mesmo estilo do painel de comissões."
      />

      <Stack spacing={3}>
        <Paper
          elevation={0}
          sx={{
            overflow: "hidden",
            borderRadius: 3,
            border: "1px solid",
            borderColor: alpha(theme.palette.primary.main, 0.22),
            background: `linear-gradient(135deg, ${alpha(
              theme.palette.primary.main,
              0.12
            )} 0%, ${alpha(theme.palette.primary.dark, 0.06)} 55%, ${alpha(
              theme.palette.background.paper,
              1
            )} 100%)`,
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", md: "center" }}
            justifyContent="space-between"
            sx={{ p: { xs: 2.5, md: 3 } }}
          >
            <Stack spacing={1.5} sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.15),
                    color: "primary.main",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <InsightsIcon />
                </Box>
                <Box>
                  <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.08 }}>
                    Relatório de vendas
                  </Typography>
                  <Typography variant="h5" component="p" sx={{ fontWeight: 800, letterSpacing: "-0.03em" }}>
                    Desempenho no período
                  </Typography>
                </Box>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip
                  icon={<CalendarMonthOutlinedIcon sx={{ fontSize: 18 }} />}
                  label={periodLabel}
                  size="small"
                  sx={{
                    fontWeight: 600,
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  }}
                />
                <Chip
                  icon={<PointOfSaleOutlinedIcon sx={{ fontSize: 18 }} />}
                  label={`${summary.saleCount} vendas · ${formatBRL(summary.totalRevenue)}`}
                  size="small"
                  variant="outlined"
                  sx={{ fontWeight: 500 }}
                />
              </Stack>
            </Stack>
            <Box sx={{ alignSelf: "center", maxWidth: 320, width: "100%" }}>
              <SalesAnalyticsHero height={150} />
            </Box>
          </Stack>
        </Paper>

        <Card elevation={0}>
          <CardContent sx={{ p: { xs: 2, sm: 2.25 } }}>
            <ReportPeriodSelector
              value={periodPreset}
              onChange={handlePresetChange}
              customFrom={customFrom}
              customTo={customTo}
              onCustomFromChange={setCustomFrom}
              onCustomToChange={setCustomTo}
            />
          </CardContent>
        </Card>

        {loadError ? (
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            {loadError}
          </Alert>
        ) : null}

        {hitQueryLimit ? (
          <Alert severity="warning" variant="outlined" sx={{ borderRadius: 2 }}>
            Foram carregadas pelo menos {SELLS_QUERY_LIMIT.toLocaleString("pt-BR")} vendas neste
            intervalo. Gráficos e totais podem estar truncados; reduza o período se precisar de
            precisão total.
          </Alert>
        ) : null}

        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Card
              elevation={0}
              sx={{
                height: "100%",
                background: `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(
                  theme.palette.background.paper,
                  1
                )} 60%)`,
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      Receita no período
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5, letterSpacing: "-0.02em" }}>
                      {formatBRL(summary.totalRevenue)}
                    </Typography>
                  </Box>
                  <PaymentsIcon sx={{ color: "primary.main", opacity: 0.9 }} />
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card
              elevation={0}
              sx={{
                height: "100%",
                background: `linear-gradient(145deg, ${alpha("#2563eb", 0.1)} 0%, ${alpha(
                  theme.palette.background.paper,
                  1
                )} 60%)`,
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      Vendas concluídas
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5, letterSpacing: "-0.02em" }}>
                      {summary.saleCount}
                    </Typography>
                  </Box>
                  <ShoppingCartOutlinedIcon sx={{ color: "primary.main", opacity: 0.85 }} />
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card
              elevation={0}
              sx={{
                height: "100%",
                background: `linear-gradient(145deg, ${alpha(theme.palette.secondary.main, 0.1)} 0%, ${alpha(
                  theme.palette.background.paper,
                  1
                )} 60%)`,
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      Ticket médio
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5, letterSpacing: "-0.02em" }}>
                      {formatBRL(summary.avgTicket)}
                    </Typography>
                  </Box>
                  <TrendingUpIcon sx={{ color: "secondary.main", opacity: 0.9 }} />
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid item xs={12} lg={8}>
            <Card elevation={0}>
              <CardContent sx={{ p: 0 }}>
                <Box sx={{ px: 2.5, py: 2, borderBottom: 1, borderColor: "divider" }}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    Receita por dia
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Soma das vendas concluídas em cada dia do intervalo.
                  </Typography>
                </Box>
                <Box sx={{ width: "100%", height: 300, p: 2 }}>
                  {summary.daily.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                      Sem dados no período.
                    </Typography>
                  ) : (
                    <ResponsiveContainer>
                      <AreaChart data={summary.daily}>
                        <defs>
                          <linearGradient id="colorRev" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={theme.palette.primary.main} stopOpacity={0.35} />
                            <stop offset="95%" stopColor={theme.palette.primary.main} stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(15,23,42,0.08)" />
                        <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                        <YAxis
                          tick={{ fontSize: 11 }}
                          tickFormatter={(v) =>
                            new Intl.NumberFormat("pt-BR", {
                              notation: "compact",
                              compactDisplay: "short",
                            }).format(v as number)
                          }
                        />
                        <Tooltip
                          formatter={(value: number) => [formatBRL(value), "Receita"]}
                          labelFormatter={(l) => `Dia ${l}`}
                        />
                        <Area
                          type="monotone"
                          dataKey="revenue"
                          stroke={theme.palette.primary.main}
                          strokeWidth={2}
                          fillOpacity={1}
                          fill="url(#colorRev)"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} lg={4}>
            <Card elevation={0} sx={{ height: "100%" }}>
              <CardContent sx={{ p: 0 }}>
                <Box sx={{ px: 2.5, py: 2, borderBottom: 1, borderColor: "divider" }}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    Formas de pagamento
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Participação por receita no período.
                  </Typography>
                </Box>
                <Box sx={{ width: "100%", height: 300, p: 2 }}>
                  {summary.byPayment.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                      Sem dados no período.
                    </Typography>
                  ) : (
                    <ResponsiveContainer>
                      <PieChart>
                        <Pie
                          data={summary.byPayment}
                          dataKey="revenue"
                          nameKey="label"
                          cx="50%"
                          cy="50%"
                          innerRadius={52}
                          outerRadius={84}
                          paddingAngle={2}
                        >
                          {summary.byPayment.map((_, i) => (
                            <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(v: number) => formatBRL(v)} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card elevation={0}>
              <CardContent sx={{ p: 0 }}>
                <Box sx={{ px: 2.5, py: 2, borderBottom: 1, borderColor: "divider" }}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    Produtos que mais geraram receita
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Top itens por valor vendido (até 8).
                  </Typography>
                </Box>
                <Box sx={{ width: "100%", height: 320, p: 2 }}>
                  {summary.topProducts.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      Nenhum produto no período selecionado.
                    </Typography>
                  ) : (
                    <ResponsiveContainer>
                      <BarChart
                        data={summary.topProducts}
                        layout="vertical"
                        margin={{ left: 8, right: 16 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(15,23,42,0.08)" />
                        <XAxis
                          type="number"
                          tickFormatter={(v) =>
                            new Intl.NumberFormat("pt-BR", {
                              notation: "compact",
                            }).format(v as number)
                          }
                        />
                        <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11 }} />
                        <Tooltip formatter={(v: number) => formatBRL(v)} />
                        <Bar dataKey="revenue" fill={theme.palette.primary.main} radius={[0, 6, 6, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card
              elevation={0}
              sx={{
                border: "1px solid",
                borderColor: alpha(theme.palette.primary.main, 0.2),
                background: `linear-gradient(135deg, ${alpha(
                  theme.palette.primary.main,
                  0.08
                )} 0%, ${alpha("#2563eb", 0.05)} 50%, ${alpha(theme.palette.background.paper, 1)} 100%)`,
              }}
            >
              <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  alignItems={{ xs: "stretch", sm: "center" }}
                  justifyContent="space-between"
                  gap={2}
                  sx={{ mb: 2 }}
                >
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.primary.main, 0.15),
                        color: "primary.main",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <AutoAwesomeIcon />
                    </Box>
                    <Box>
                      <Typography variant="h6" fontWeight={700}>
                        Insights com IA
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Análise em linguagem natural com base nos números do período (Gemini no
                        navegador).
                      </Typography>
                    </Box>
                  </Stack>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AutoAwesomeIcon />}
                    disabled={!aiInsightsEnabled || aiLoading}
                    onClick={() => runInsights()}
                    sx={{ alignSelf: { xs: "stretch", sm: "center" }, flexShrink: 0 }}
                  >
                    {aiInsightsEnabled ? "Gerar insights" : "Módulo de IA não ativo"}
                  </Button>
                </Stack>

                {!aiInsightsEnabled ? (
                  <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
                    Ative o módulo Cadastro em massa e IA em Configurações para gerar insights com IA
                    neste painel.
                  </Alert>
                ) : null}

                {aiError ? (
                  <Alert severity="warning" sx={{ mt: 1, borderRadius: 2 }}>
                    {aiError}
                  </Alert>
                ) : null}

                {aiText ? (
                  <Box
                    sx={{
                      mt: 2,
                      p: 2,
                      borderRadius: 2,
                      bgcolor: "background.paper",
                      border: "1px solid",
                      borderColor: "divider",
                      whiteSpace: "pre-wrap",
                      fontSize: 14,
                      lineHeight: 1.65,
                    }}
                  >
                    {aiText}
                  </Box>
                ) : null}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card elevation={0}>
              <CardContent sx={{ p: 0 }}>
                <Box sx={{ px: 2.5, py: 2, borderBottom: 1, borderColor: "divider" }}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    Vendas recentes no período
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Até 20 últimas concluídas no intervalo selecionado.
                  </Typography>
                </Box>
                <Box sx={{ p: 2 }}>
                  {summary.sellsInPeriod.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      Nenhuma venda concluída neste intervalo.
                    </Typography>
                  ) : (
                    <Stack spacing={1.25}>
                      {summary.sellsInPeriod.slice(0, 20).map((s) => {
                        const done = s.getCompletedAt();
                        return (
                          <Stack
                            key={s.id}
                            direction={{ xs: "column", sm: "row" }}
                            alignItems={{ sm: "center" }}
                            justifyContent="space-between"
                            sx={{
                              p: 1.75,
                              borderRadius: 2,
                              border: "1px solid",
                              borderColor: "divider",
                              bgcolor: alpha(theme.palette.primary.main, 0.02),
                              gap: 1,
                              transition: "box-shadow 0.2s",
                              "&:hover": {
                                boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.08)}`,
                              },
                            }}
                          >
                            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
                              <Box
                                sx={{
                                  width: 40,
                                  height: 40,
                                  borderRadius: 2,
                                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                                  color: "primary.main",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  flexShrink: 0,
                                }}
                              >
                                <PointOfSaleOutlinedIcon fontSize="small" />
                              </Box>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography fontWeight={700} noWrap>
                                  {formatBRL(s.getSellTotal())}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block" noWrap>
                                  {done ? done.format("DD/MM/YYYY HH:mm") : "—"} · {s.id.slice(0, 8)}…
                                </Typography>
                              </Box>
                            </Stack>
                            <Button
                              component={RouterLink}
                              to={`/vendas/${s.id}`}
                              size="small"
                              endIcon={<OpenInNewIcon fontSize="small" />}
                              variant="outlined"
                              sx={{ flexShrink: 0 }}
                            >
                              Detalhes
                            </Button>
                          </Stack>
                        );
                      })}
                    </Stack>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Box sx={{ pb: 2 }} />
      </Stack>
    </Shell>
  );
};
